import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Database, Brain, MessageSquare, Workflow, Shield, Lock, Plug, ChevronRight, BarChart3, Zap } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const SemanticGraph = () => {
  const nodes = [
    { cx: 200, cy: 150, r: 10 }, { cx: 100, cy: 80, r: 6 }, { cx: 300, cy: 80, r: 6 },
    { cx: 80, cy: 200, r: 5 }, { cx: 320, cy: 200, r: 5 }, { cx: 150, cy: 260, r: 7 },
    { cx: 250, cy: 260, r: 7 }, { cx: 50, cy: 130, r: 4 }, { cx: 350, cy: 130, r: 4 },
    { cx: 200, cy: 50, r: 5 }, { cx: 120, cy: 180, r: 4 }, { cx: 280, cy: 180, r: 4 },
  ];
  const edges = [[0,1],[0,2],[0,3],[0,4],[0,5],[0,6],[1,7],[2,8],[1,9],[2,9],[5,10],[6,11],[10,3],[11,4]];
  return (
    <svg viewBox="0 0 400 310" className="w-full h-full opacity-80">
      <defs>
        <radialGradient id="ng"><stop offset="0%" stopColor="#00d4ff" stopOpacity="0.8"/><stop offset="100%" stopColor="#00d4ff" stopOpacity="0"/></radialGradient>
      </defs>
      {edges.map(([a,b],i) => (
        <line key={i} x1={nodes[a].cx} y1={nodes[a].cy} x2={nodes[b].cx} y2={nodes[b].cy} stroke="#00d4ff" strokeWidth="0.8" opacity="0.25" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="8" to="0" dur={`${2+i*0.3}s`} repeatCount="indefinite"/>
        </line>
      ))}
      {nodes.map((n,i) => (
        <g key={i}>
          <circle cx={n.cx} cy={n.cy} r={n.r*3} fill="url(#ng)" opacity="0.2"/>
          <circle cx={n.cx} cy={n.cy} r={n.r} fill="#00d4ff" opacity="0.7">
            <animate attributeName="opacity" values="0.5;1;0.5" dur={`${2+i*0.2}s`} repeatCount="indefinite"/>
          </circle>
        </g>
      ))}
    </svg>
  );
};

const features = [
  { icon: Brain, title: 'AI-Powered Semantic Discovery', desc: 'Auto-profile schemas, infer relationships, and build a living data glossary without manual effort.' },
  { icon: MessageSquare, title: 'Conversational Analytics', desc: 'Ask questions in natural language. Get SQL queries, explainable answers, and session memory.' },
  { icon: Workflow, title: 'Visual Workflow Canvas', desc: 'Drag-and-drop data pipelines with visual orchestration and real-time execution monitoring.' },
  { icon: Shield, title: 'Human-Gated Governance', desc: 'Every AI-generated insight passes through human review before reaching production.' },
  { icon: Plug, title: '40+ Data Connectors', desc: 'PostgreSQL, BigQuery, Snowflake, MongoDB, S3, and dozens more. Connect in seconds.' },
  { icon: Lock, title: 'Enterprise Security', desc: 'SOC 2 compliant, role-based access, audit logging, and end-to-end encryption.' },
];

const metrics = [
  { value: '3s', label: 'Query P95 Latency' },
  { value: '99.5%', label: 'Connector Reliability' },
  { value: '40+', label: 'Database Integrations' },
  { value: '<2min', label: 'Time to First Query' },
];

export default function Landing() {
  return (
    <div className="bg-[#0a0a1a] overflow-hidden">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 hero-grid" />
        <div className="glow-spot w-[500px] h-[500px] bg-[#00d4ff] top-[-100px] left-[-100px] opacity-20" />
        <div className="glow-spot w-[400px] h-[400px] bg-[#3b82f6] bottom-[-100px] right-[-100px] opacity-15" style={{ animationDelay: '2s' }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-2 gap-12 items-center py-20">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-6">
              The Future of Data Intelligence
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[0.95] mb-6" style={{ fontFamily: 'Outfit' }}>
              The AI-Native<br />Data Intelligence<br /><span className="gradient-text">Workbench</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400 leading-relaxed max-w-lg mb-8">
              One unified platform to connect, discover, and query all your data. Replace your fragmented stack with intelligent automation.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/signup">
                <Button data-testid="hero-get-started-btn" className="bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full px-8 py-3 h-12 text-base hover:opacity-90 shadow-[0_0_30px_rgba(0,212,255,0.3)]">
                  Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-book-demo-btn" variant="outline" className="border-white/20 text-white rounded-full px-8 py-3 h-12 text-base hover:bg-white/5 bg-transparent">
                  Book a Demo
                </Button>
              </Link>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:block">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#00d4ff]/10 to-[#3b82f6]/10 rounded-3xl blur-xl" />
              <div className="relative glass-card p-8">
                <SemanticGraph />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section data-testid="problem-section" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center max-w-3xl mx-auto">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-red-400 font-bold mb-4">The Problem</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              The $47B data stack is <span className="text-red-400">broken</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mb-12">
              Enterprises juggle 15+ tools for ETL, cataloging, querying, and governance. The result? Silos, slow insights, and skyrocketing costs.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Database, title: 'Tool Sprawl', desc: '15+ disconnected tools per enterprise data stack, each with its own learning curve and contracts.' },
              { icon: BarChart3, title: 'Slow Insights', desc: 'Days to weeks from question to answer. Data teams bottleneck every business decision.' },
              { icon: Zap, title: 'Rising Costs', desc: '$47B spent annually on data infrastructure. 70% wasted on integration and maintenance.' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="glass-card p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section data-testid="solution-section" className="py-24 md:py-32 relative bg-[#111122]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center max-w-3xl mx-auto mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">The Solution</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              One platform. <span className="gradient-text">Infinite insight.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed">
              DQP replaces your fragmented data stack with a unified, AI-powered workbench that connects, discovers, and queries all your data from a single interface.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { step: '01', title: 'Connect', desc: 'Plug in your databases, data lakes, and APIs with 40+ pre-built connectors.', color: '#00d4ff' },
              { step: '02', title: 'Discover', desc: 'AI auto-discovers schemas, relationships, and builds a semantic knowledge graph.', color: '#3b82f6' },
              { step: '03', title: 'Query', desc: 'Ask questions in natural language. Get instant, explainable, governed answers.', color: '#8b5cf6' },
            ].map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="glass-card p-8 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1" style={{ background: `linear-gradient(90deg, ${s.color}, transparent)` }} />
                <span className="text-6xl font-black opacity-5 absolute top-4 right-6" style={{ fontFamily: 'Outfit', color: s.color }}>{s.step}</span>
                <span className="text-sm font-bold mb-3 block" style={{ color: s.color }}>{s.step}</span>
                <h3 className="text-white font-bold text-xl mb-3" style={{ fontFamily: 'Outfit' }}>{s.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                <ChevronRight className="w-5 h-5 mt-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: s.color }} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section data-testid="features-section" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center max-w-2xl mx-auto mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Capabilities</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Everything you need to master your data
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeUp} data-testid={`feature-card-${i}`} className="glass-card p-8 group">
                <div className="w-12 h-12 rounded-xl bg-[#00d4ff]/10 flex items-center justify-center mb-5 group-hover:bg-[#00d4ff]/20 transition-colors">
                  <f.icon className="w-6 h-6 text-[#00d4ff]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Social Proof / Metrics */}
      <section data-testid="social-proof-section" className="py-24 md:py-32 bg-[#111122]/50 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Trusted by teams everywhere</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center items-center gap-12 mt-8 opacity-40">
              {['Acme Corp', 'Globex', 'Initech', 'Umbrella', 'Stark Ind'].map(name => (
                <span key={name} className="text-white/60 text-lg font-semibold tracking-wide" style={{ fontFamily: 'Outfit' }}>{name}</span>
              ))}
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {metrics.map((m, i) => (
              <motion.div key={i} variants={fadeUp} data-testid={`metric-${i}`} className="glass-card p-8 text-center metric-glow">
                <p className="text-4xl font-black gradient-text mb-2" style={{ fontFamily: 'Outfit' }}>{m.value}</p>
                <p className="text-slate-500 text-sm">{m.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section data-testid="trust-badges-section" className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center items-center gap-8">
            {[
              { label: 'SOC 2 Type II', icon: Shield },
              { label: 'HIPAA Ready', icon: Lock },
              { label: 'Enterprise-Grade', icon: Shield },
            ].map((badge, i) => (
              <div key={i} data-testid={`trust-badge-${i}`} className="flex items-center gap-3 px-6 py-3 rounded-full border border-white/10 bg-white/[0.02]">
                <badge.icon className="w-4 h-4 text-emerald-400" />
                <span className="text-slate-400 text-sm font-medium">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a1a] via-[#111122] to-[#0a0a1a]" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Ready to retire your<br /><span className="gradient-text">data stack?</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg mb-8">
              Join the teams already using DQP to cut data tool costs by 60% and accelerate insights by 10x.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/signup">
                <Button data-testid="cta-get-started-btn" className="bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full px-8 py-3 h-12 text-base hover:opacity-90 shadow-[0_0_30px_rgba(0,212,255,0.3)]">
                  Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
